import {
  type ApiToken,
  type CreateTokenResponse,
  createApiToken,
  revokeApiToken,
  tokenKeys,
  useApiTokens,
} from '@/services/agent/hooks';
import rem from '@/utils/rem';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

const ALL_SCOPES = [
  { value: 'read:actions', label: 'Read actions & blocks' },
  { value: 'write:actions', label: 'Create/edit/delete actions' },
  { value: 'read:categories', label: 'Read categories' },
  { value: 'read:projects', label: 'Read projects' },
  { value: 'read:people', label: 'Read people & promises' },
  { value: 'write:people', label: 'Create promises' },
  { value: 'agent:chat', label: 'Use AI agent chat' },
];

function ApiKeysSettings() {
  const { data: tokens } = useApiTokens();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [isCreating, setIsCreating] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    'read:actions',
    'read:categories',
    'read:projects',
    'read:people',
    'agent:chat',
  ]);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const { onCopy, hasCopied } = useClipboard(createdToken || '');

  const handleCreate = useCallback(async () => {
    if (!newTokenName.trim()) return;

    const result = await createApiToken(newTokenName.trim(), selectedScopes);
    if (result?.token) {
      setCreatedToken(result.token);
      setNewTokenName('');
      setIsCreating(false);
      queryClient.invalidateQueries({ queryKey: tokenKeys.list });
      toast({
        title: 'API key created',
        description: "Copy it now — you won't be able to see it again.",
        status: 'success',
        duration: 5000,
      });
    }
  }, [newTokenName, selectedScopes, queryClient, toast]);

  const handleRevoke = useCallback(
    async (tokenId: string, prefix: string) => {
      await revokeApiToken(tokenId);
      queryClient.invalidateQueries({ queryKey: tokenKeys.list });
      toast({
        title: `Key ${prefix}... revoked`,
        status: 'info',
        duration: 3000,
      });
    },
    [queryClient, toast],
  );

  const handleDismissToken = useCallback(() => {
    setCreatedToken(null);
  }, []);

  const apiBaseUrl =
    import.meta.env.VITE_AGENT_API_URL || `${import.meta.env.VITE_REST_API_URL?.replace(/\/api$/, '')}/api/agent`;

  return (
    <VStack alignItems="stretch" spacing={3}>
      {/* API endpoint info */}
      <Box bg="whiteAlpha.50" borderRadius="md" p={3}>
        <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>
          API Endpoint
        </Text>
        <Text fontSize="xs" fontFamily="mono" color="cyan.400" userSelect="all">
          {apiBaseUrl}/chat
        </Text>
        <Text fontSize="2xs" color="gray.500" mt={1}>
          Send POST requests with your API key as Bearer token. Supports SSE streaming.
        </Text>
      </Box>

      {/* Newly created token banner */}
      {createdToken && (
        <Box bg="green.800" borderRadius="md" p={3}>
          <Text fontSize="xs" fontWeight="bold" color="green.200" mb={1}>
            New API Key — copy it now, you won't see it again:
          </Text>
          <Flex gap={2}>
            <Input
              value={createdToken}
              isReadOnly
              size="xs"
              fontFamily="mono"
              fontSize="xs"
              bg="whiteAlpha.100"
              color="white"
              userSelect="all"
            />
            <Button size="xs" colorScheme="green" onClick={onCopy} minW="60px">
              {hasCopied ? 'Copied!' : 'Copy'}
            </Button>
          </Flex>
          <Text fontSize="2xs" color="green.300" mt={2} fontFamily="mono" userSelect="all">
            curl -X POST {apiBaseUrl}/chat \{'\n'}
            {'  '}-H "Authorization: Bearer {createdToken?.substring(0, 20)}..." \{'\n'}
            {'  '}-H "Content-Type: application/json" \{'\n'}
            {'  '}-d '{'{'}' "message": "What are my actions today?" {'}'}'
          </Text>
          <Button size="xs" variant="ghost" colorScheme="whiteAlpha" mt={1} onClick={handleDismissToken}>
            Dismiss
          </Button>
        </Box>
      )}

      {/* Existing tokens */}
      {tokens && tokens.length > 0 ? (
        tokens.map((token: ApiToken) => (
          <Flex
            key={token.id}
            justify="space-between"
            align="center"
            bg="whiteAlpha.50"
            borderRadius="md"
            px={3}
            py={2}
          >
            <Box flex={1} overflow="hidden">
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {token.name}
                </Text>
                {!token.is_active && (
                  <Badge colorScheme="red" fontSize="2xs">
                    Revoked
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.400" fontFamily="mono">
                {token.token_prefix}...
              </Text>
              {token.last_used_at && (
                <Text fontSize="2xs" color="gray.500">
                  Last used: {new Date(token.last_used_at).toLocaleDateString()}
                </Text>
              )}
            </Box>
            {token.is_active && (
              <IconButton
                aria-label="Revoke key"
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleRevoke(token.id, token.token_prefix)}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                }
              />
            )}
          </Flex>
        ))
      ) : !isCreating ? (
        <Text fontSize="sm" color="gray.500">
          No API keys yet. Create one to connect external apps.
        </Text>
      ) : null}

      {/* Create form */}
      {isCreating ? (
        <VStack align="stretch" bg="whiteAlpha.50" borderRadius="md" p={3} spacing={3}>
          <Input
            placeholder="Key name (e.g. My Bot)"
            size="sm"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
          />
          <Box>
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>
              Permissions
            </Text>
            <CheckboxGroup value={selectedScopes} onChange={(v) => setSelectedScopes(v as string[])}>
              <VStack align="start" spacing={1}>
                {ALL_SCOPES.map((scope) => (
                  <Checkbox key={scope.value} value={scope.value} size="sm">
                    <Text fontSize="xs">{scope.label}</Text>
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          </Box>
          <HStack>
            <Button size="sm" colorScheme="blue" onClick={handleCreate} isDisabled={!newTokenName.trim()}>
              Create Key
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      ) : (
        <Button size="sm" variant="outline" colorScheme="blue" onClick={() => setIsCreating(true)}>
          + Create API Key
        </Button>
      )}
    </VStack>
  );
}

export default ApiKeysSettings;
